export type InventoryBoxEventKind =
  | "created"
  | "quantity"
  | "reprint"
  | "split"
  | "combine"
  | "putaway"
  | "release_to_floor"
  | "consume";

export type InventoryBoxEventSummaryInput = {
  event_kind: InventoryBoxEventKind;
  quantity_before: number | null;
  quantity_after: number | null;
  related_short_code?: string | null;
  detail?: string | null;
};

/** Stored created lines look like "split | C06F-01E5". The history table shows the verb and the source apart. */
export function inventoryBoxCreatedHistoryParts(event: {
  event_kind: string;
  detail?: string | null;
}): { event: string; detail: string } | null {
  if (event.event_kind !== "created") return null;
  const trimmed = event.detail?.trim() ?? "";
  const separator = " | ";
  const index = trimmed.indexOf(separator);
  if (index <= 0) return null;
  const verb = trimmed.slice(0, index);
  const rest = trimmed.slice(index + separator.length).trim();
  if (rest === "") return null;
  if (verb === "split") return { event: "split", detail: `from ${rest}` };
  if (verb === "received" || verb === "carved" || verb === "work order") {
    return { event: verb, detail: rest };
  }
  return null;
}

export function inventoryBoxHistoryQuantity(event: {
  quantity_before: number | null;
  quantity_after: number | null;
}): string {
  if (event.quantity_before != null && event.quantity_after != null) {
    return `${String(event.quantity_before)} to ${String(event.quantity_after)}`;
  }
  if (event.quantity_after != null) return String(event.quantity_after);
  if (event.quantity_before != null) return String(event.quantity_before);
  return "";
}

export function inventoryBoxEventSummary(event: InventoryBoxEventSummaryInput): string {
  if (event.event_kind === "created") {
    const recorded = inventoryBoxCreatedHistoryParts(event);
    if (recorded != null) {
      if (recorded.event === "split") return `Split ${recorded.detail}`;
      if (recorded.event === "received") return `Received ${recorded.detail}`;
      if (recorded.event === "carved") return `Carved, ${recorded.detail}`;
      return `Work order ${recorded.detail}`;
    }
    const from = event.related_short_code?.trim() ?? "";
    if (event.quantity_after != null && from !== "") {
      return `Split from ${from} with quantity ${String(event.quantity_after)}`;
    }
    return event.quantity_after != null
      ? `Created with quantity ${String(event.quantity_after)}`
      : "Created";
  }
  if (event.event_kind === "reprint") {
    return "Reprinted label";
  }
  if (event.event_kind === "putaway") {
    const detail = event.detail?.trim() ?? "";
    return detail !== "" ? detail : "Put away on shelf";
  }
  if (event.event_kind === "release_to_floor") {
    const detail = event.detail?.trim() ?? "";
    return detail !== "" ? `Released to floor. ${detail}` : "Released to floor";
  }
  if (event.event_kind === "consume") {
    const detail = event.detail?.trim() ?? "";
    return detail !== "" ? detail : "Consumed";
  }
  if (event.event_kind === "split") {
    const parts = event.detail?.trim() ?? "";
    const remainder = event.quantity_after != null ? `this carton ${String(event.quantity_after)}` : "split";
    return parts !== "" ? `Split: ${remainder}; ${parts}` : `Split: ${remainder}`;
  }
  if (event.event_kind === "combine") {
    const detail = event.detail?.trim() ?? "";
    if (detail !== "") return detail;
    const other = event.related_short_code?.trim() ?? "";
    return other !== "" ? `Combined with ${other}` : "Combined";
  }
  if (event.quantity_before != null && event.quantity_after != null) {
    const reason = event.detail?.trim() ?? "";
    const base = `Quantity ${String(event.quantity_before)} to ${String(event.quantity_after)}`;
    return reason !== "" ? `${base}. Reason: ${reason}` : base;
  }
  return "Quantity changed";
}
